import { nextTick, ref } from 'vue'
import type { Ref } from 'vue'
import Swiper from 'swiper'
import { Autoplay } from 'swiper/modules'

/**
 * 底部轮播 Swiper 封装
 * 用于支付弹窗底部的单行轮播区域，管理初始化、销毁和分页指示器状态
 *
 * @param swiperRef  绑定到 swiper 容器元素的模板 ref
 * @param getItems   返回当前轮播数据列表的函数（在 init 时调用以获取最新值）
 *
 * 使用递增 token 防止同一响应式 flush 内多个 watch 同时触发 init() 导致
 * 在同一 DOM 元素上创建多个 Swiper 实例（会残留 loop 克隆节点，使指示器失效）。
 * 只有最后一次 init() 对应的 rAF 回调才会真正创建 Swiper 实例。
 */
export function useBottomSwiper(
  swiperRef: Ref<HTMLElement | undefined>,
  getItems: () => any[],
) {
  const swiperInstance = ref<Swiper | null>(null)
  const slideIndex = ref(0)
  let initToken = 0

  function init() {
    const token = ++initToken

    if (swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
      swiperInstance.value = null
    }
    slideIndex.value = 0

    nextTick(() => {
      requestAnimationFrame(() => {
        // 若在等待期间 init() 又被调用，则此次已过期，直接跳过
        if (token !== initToken)
          return

        const items = getItems()
        if (!items?.length || !swiperRef.value)
          return

        swiperInstance.value = new Swiper(swiperRef.value, {
          modules: [Autoplay],
          spaceBetween: 12,
          autoplay: {
            delay: 3500,
            disableOnInteraction: false,
          },
          loop: items.length > 1,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          resizeObserver: true,
          on: {
            slideChange(swiper) {
              slideIndex.value = swiper.realIndex
            },
            slideChangeTransitionEnd(swiper) {
              const activeSlide = swiper.slides[swiper.activeIndex]
              if (activeSlide) {
                const idx = Number.parseInt(activeSlide.getAttribute('data-swiper-slide-index') || '0', 10)
                slideIndex.value = idx
              }
            },
          },
        })
      })
    })
  }

  function destroy() {
    ++initToken // 取消所有挂起的 init
    if (swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
      swiperInstance.value = null
    }
  }

  function onBulletClick(index: number) {
    swiperInstance.value?.slideToLoop(index)
  }

  return { swiperInstance, slideIndex, init, destroy, onBulletClick }
}
